import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ArrowLeft } from "@phosphor-icons/react";
import { EmployeeHeader, TempPasswordBanner } from "../../components/admin/employee-detail/HeaderCard";
import { PersonalTab } from "../../components/admin/employee-detail/PersonalTab";
import { JobTab } from "../../components/admin/employee-detail/JobTab";
import { BankTab } from "../../components/admin/employee-detail/BankTab";
import { SalaryTab } from "../../components/admin/employee-detail/SalaryTab";
import { DocumentsTab } from "../../components/admin/employee-detail/DocumentsTab";

const TABS = ["Personal", "Job", "Bank", "Salary", "Documents"];

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState("Personal");
  const [sal, setSal] = useState({ basic: 0, hra: 0, special_allowance: 0, pf_applicable: true, esic_applicable: false });
  const [balances, setBalances] = useState([]);
  const [tempPw, setTempPw] = useState("");

  useEffect(() => {
    api.get(`/employees/${id}`).then((r) => {
      setEmp(r.data);
      if (r.data.salary) setSal(r.data.salary);
    });
    api.get(`/leave/balance/${id}`).then((r) => setBalances(r.data)).catch(() => {});
  }, [id]);

  if (!emp) return <div className="text-stone-400">Loading…</div>;

  const set = (k) => (e) => setEmp({ ...emp, [k]: e.target.value });

  const save = async () => {
    try {
      const { id: _, email, salary, created_at, ...body } = emp;
      const r = await api.put(`/employees/${id}`, body);
      setEmp(r.data);
      toast.success("Profile saved");
    } catch (e) { toast.error(errText(e)); }
  };

  const saveSalary = async () => {
    try {
      await api.put(`/employees/${id}/salary`, { ...sal, basic: +sal.basic, hra: +sal.hra, special_allowance: +sal.special_allowance });
      toast.success("Salary structure saved");
    } catch (e) { toast.error(errText(e)); }
  };

  const resetPassword = async () => {
    let payload = {};
    if (me?.role === "super_admin") {
      const typed = window.prompt(`Reset ${emp.name}'s password — their current password stops working immediately.\n\nType a new temporary password (min 8 chars, letters + numbers), or leave BLANK to auto-generate one:`);
      if (typed === null) return;
      if (typed.trim()) payload = { new_password: typed.trim() };
    } else if (!window.confirm(`Reset ${emp.name}'s password? Their current password will stop working immediately.`)) return;
    try {
      const r = await api.post(`/employees/${id}/reset-password`, payload);
      setTempPw(r.data.temp_password);
      toast.success("Temporary password set");
    } catch (e) { toast.error(errText(e)); }
  };

  const deleteEmployee = async () => {
    const typed = window.prompt(`This PERMANENTLY deletes ${emp.name} and ALL their records (attendance, leave, payslips, documents, loans). This cannot be undone.\n\nType DELETE to confirm:`);
    if (typed !== "DELETE") return;
    try {
      const r = await api.delete(`/employees/${id}`);
      toast.success(r.data.message);
      navigate("/admin/employees", { replace: true });
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="employee-detail-page">
      <Link to="/admin/employees" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-500 hover:text-primary transition-colors duration-200" data-testid="back-to-employees">
        <ArrowLeft size={16} /> Back to directory
      </Link>

      <EmployeeHeader emp={emp} balances={balances}
        showDelete={emp.role !== "super_admin" || (me?.role === "super_admin" && me?.id !== id)}
        onResetPassword={resetPassword} onDelete={deleteEmployee} />

      <TempPasswordBanner tempPw={tempPw} name={emp.name} />

      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`employee-tab-${t.toLowerCase()}`}
            className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors duration-200 ${tab === t ? "bg-secondary text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>{t}</button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-6">
        {tab === "Personal" && <PersonalTab emp={emp} set={set} />}
        {tab === "Job" && <JobTab emp={emp} set={set} canEditRoles={me?.role === "super_admin"} />}
        {tab === "Bank" && <BankTab emp={emp} set={set} />}
        {tab === "Salary" && <SalaryTab sal={sal} setSal={setSal} onSave={saveSalary} />}
        {tab === "Documents" && <DocumentsTab empId={id} />}
        {tab !== "Salary" && tab !== "Documents" && (
          <button onClick={save} data-testid="save-employee-button" className="tap-scale mt-6 px-6 py-3 rounded-full bg-primary text-white font-bold hover:bg-[#A63F07] transition-colors duration-200">Save changes</button>
        )}
      </div>
    </div>
  );
}
